import { ActionConfirmation } from '@app/components/base/ActionConfirmation';
import { Panel } from '@app/components/base/content-structure/Panel';
import { NullMessageMui } from '@mi-front/ui';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Alert, Button, IconButton, Stack, Typography } from '@mui/material';
import {
  ArrayPath,
  Control,
  FieldArray,
  FieldErrors,
  FieldPath,
  FieldValues,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form-mui';
import { useIntl } from 'react-intl';

type ArrayItem<
  TFieldValues extends FieldValues,
  TName extends ArrayPath<TFieldValues>,
> = FieldArray<TFieldValues, TName>;

export interface FormArrayFieldMuiProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends ArrayPath<TFieldValues> = ArrayPath<TFieldValues>,
> {
  name: TName;
  title?: string;
  emptyMessage?: string;
  addButtonLabel?: string;
  defaultItem: ArrayItem<TFieldValues, TName>;
  renderItem: (props: { index: number; onRemove: () => void }) => React.ReactNode;
  deleteConfirmation?: {
    title: string;
    description: string | ((item: ArrayItem<TFieldValues, TName>, index: number) => string);
    confirmText?: string;
    cancelText?: string;
  };
  getItemDisplayName?: (item: ArrayItem<TFieldValues, TName>, index: number) => string;
}

/**
 * Editable field-array list. Each row is a Panel (title + delete actions);
 * the add control sits below the list.
 */
export function FormArrayItem<
  TFieldValues extends FieldValues = FieldValues,
  TName extends ArrayPath<TFieldValues> = ArrayPath<TFieldValues>,
>({
  name,
  title,
  emptyMessage,
  addButtonLabel,
  defaultItem,
  renderItem,
  deleteConfirmation,
  getItemDisplayName,
}: FormArrayFieldMuiProps<TFieldValues, TName>) {
  const { $t } = useIntl();
  const {
    control,
    formState: { errors },
  } = useFormContext<TFieldValues>();
  const { fields, append, remove } = useFieldArray({
    control: control as Control<TFieldValues>,
    name,
  });
  // Live form values so panel titles track name edits (fields[] stays at append-time values).
  // Cast: ArrayPath is a FieldPath, but useWatch overloads do not accept ArrayPath directly.
  const watchedItems = useWatch({
    control,
    name: name as FieldPath<TFieldValues>,
  }) as ArrayItem<TFieldValues, TName>[] | undefined;

  const fieldErrors = errors[name as string] as FieldErrors<TFieldValues> | undefined;
  const rootErrorMessage =
    fieldErrors?.root && typeof fieldErrors.root === 'object' && 'message' in fieldErrors.root
      ? String(fieldErrors.root.message ?? '')
      : undefined;

  const resolvedAddButtonLabel =
    addButtonLabel ??
    $t({
      id: 'form.array.addButtonLabel',
      defaultMessage: 'Agregar',
    });

  function handleAdd() {
    append(defaultItem);
  }

  function handleRemove(index: number) {
    remove(index);
  }

  function getPanelTitle(item: ArrayItem<TFieldValues, TName>, index: number) {
    if (getItemDisplayName) {
      return getItemDisplayName(item, index);
    }
    return String(index + 1);
  }

  function renderDeleteButton(index: number, item: ArrayItem<TFieldValues, TName>) {
    const onRemove = () => handleRemove(index);

    if (deleteConfirmation) {
      const description =
        typeof deleteConfirmation.description === 'function'
          ? deleteConfirmation.description(item, index)
          : deleteConfirmation.description;

      const itemName = getItemDisplayName ? getItemDisplayName(item, index) : undefined;
      const finalDescription = itemName ? description.replace('{name}', itemName) : description;

      return (
        <ActionConfirmation
          trigger={(onClick: (event: React.MouseEvent<HTMLElement>) => void) => (
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                onClick(e);
              }}
            >
              <DeleteIcon />
            </IconButton>
          )}
          title={deleteConfirmation.title}
          description={finalDescription}
          onConfirm={onRemove}
          confirmText={
            deleteConfirmation.confirmText ??
            $t({ id: 'common.delete', defaultMessage: 'Eliminar' })
          }
          cancelText={
            deleteConfirmation.cancelText ?? $t({ id: 'common.cancel', defaultMessage: 'Cancelar' })
          }
          icon="warning"
          iconColor="#ff7d55"
          placement="bottom-start"
        />
      );
    }

    return (
      <IconButton size="small" color="error" onClick={onRemove}>
        <DeleteIcon />
      </IconButton>
    );
  }

  return (
    <Stack spacing={3}>
      {fields.length > 0 && (
        <Stack spacing={2}>
          {fields.map((field, index) => {
            const item = (watchedItems?.[index] ?? field) as ArrayItem<TFieldValues, TName>;
            return (
              <Panel
                key={field.id}
                title={getPanelTitle(item, index)}
                actions={renderDeleteButton(index, item)}
              >
                {renderItem({
                  index,
                  onRemove: () => handleRemove(index),
                })}
              </Panel>
            );
          })}
        </Stack>
      )}

      {/* Optional empty hint — prefer omitting it when a ContentCluster already labels the section. */}
      {fields.length === 0 && emptyMessage && <NullMessageMui>{emptyMessage}</NullMessageMui>}

      {(title || resolvedAddButtonLabel) && (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {title && <Typography variant="subtitle2">{title}</Typography>}
          <Button startIcon={<AddIcon />} onClick={handleAdd} variant="outlined" size="small">
            {resolvedAddButtonLabel}
          </Button>
        </Stack>
      )}

      {fieldErrors?.root && (
        <Alert severity="error">
          {rootErrorMessage ||
            $t({
              id: 'form.array.fieldError',
              defaultMessage: 'Error en el campo',
            })}
        </Alert>
      )}
    </Stack>
  );
}
